// components/tables/tablesAgentContext.ts
// Builds a compact text snapshot of the OPEN Analyst table for the Dealer
// agent's context (injected into the prompt's `situation`). This is what lets
// the agent SEE the table the user is looking at, its sheets and every cell as
// A1=value/formula, so it can act on relative instructions ("add a total row",
// "fill column B", "make this a formula") and edit existing cells correctly.
// Reads the client-side tables store directly; returns null when no table is open.
'use client';

import { useTablesStore } from './tablesStore';
import { computeWorkbook } from '../asset-management/workbookEngine';
import { keyToA1 } from './cellRef';

// Format a computed cell value compactly for the model, kills float noise, caps length, keeps
// error strings (#DIV/0!, #VALUE!, …) verbatim so the agent can spot and debug them.
function fmtComputed(v: unknown): string {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'boolean') return v ? 'TRUE' : 'FALSE';
  if (typeof v === 'string') return v.length > 40 ? `${v.slice(0, 40)}…` : v;
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return String(v);
    const abs = Math.abs(v);
    return v.toLocaleString('en-US', { maximumFractionDigits: abs !== 0 && abs < 1 ? 6 : 2 });
  }
  return String(v);
}

const MAX_SHEETS = 8;
const MAX_CELLS = 80; // cells shown per non-active sheet
const MAX_CELLS_ACTIVE = 1000; // the sheet in view: show (nearly) all of it so the agent sees the real data
const MAX_CELL_LEN = 200; // cap per-cell serialized length so long text/formula cells can't blow up the prompt

/**
 * Snapshot of the open Analyst table, or null when none is open. Bounded in size.
 * `forMaster` + `sessionId` build the snapshot from the doc BOUND to that master
 * session, even though the Analyst WORKSPACE is not mounted (TablesWorkspace never
 * mounts for a master session, so `isAnalystOpen` stays false). The Master builds
 * models headlessly in its own chat and must read its OWN live cells + computed
 * results back each turn to verify; using the session-bound doc also stops two
 * master sessions from seeing each other's model. Without this the Master builds blind.
 */
export function buildAnalystContext(options?: { forMaster?: boolean; sessionId?: string }): string | null {
  const st = useTablesStore.getState();
  // A Master turn targets the doc BOUND to its session (built headlessly in chat),
  // never the global activeDocId. Every other turn uses the doc open in the Analyst tool.
  let id: string | null;
  if (options?.forMaster) {
    id = (options.sessionId && st.masterSessionDocs[options.sessionId]) || null;
  } else {
    if (!st.isAnalystOpen) return null;
    id = st.activeDocId;
  }
  if (!id) return null;
  const doc = st.docs[id];
  if (!doc) return null;

  // Compute the whole workbook so the agent sees each formula's RESULT (and live errors), not just
  // its source, this is what lets it analyse, debug, and cite real numbers. Best-effort: a compute
  // failure simply omits the results, leaving the formulas visible.
  let wb: Record<string, Record<string, unknown>> | null = null;
  try {
    wb = computeWorkbook(doc.sheets.map((s) => ({ name: s.name, cells: s.cells }))) as unknown as Record<
      string,
      Record<string, unknown>
    >;
  } catch {
    wb = null;
  }

  const out: string[] = [];
  if (doc.propertyId) out.push(`SHARED ASSET WORKBOOK: propertyId=${doc.propertyId}. Access: ${doc.sharedAccess ?? 'reader'}. Rent Roll and Lease Dates are permanent. Reuse this canonical workbook on every request; never rebuild it or create a personal copy. Blank cells are unknown recorded facts, not zero. Owner edits save to the shared asset; readers cannot write.`);
  const surfaceFraming = options?.forMaster
    ? '=== ANALYST MODEL (the live spreadsheet you are building for the user in THIS chat). ' +
      'It is NOT displayed on the user\'s screen: they see only the slices you surface with ' +
      'ANALYST_SHOW_IN_CHAT, so when the model is ready SHOW the key output rather than only describing it. '
    : '=== ANALYST TABLE (live in the Analyst tool; the user is looking at it right now). ' +
      'This is the standalone Analyst spreadsheet tool, a DIFFERENT surface from any property\'s Asset Model. ' +
      'Work ONLY on the table below; do NOT reference Asset-Model tabs (Projection Summary, Cashflow, etc.) ' +
      'and do NOT claim the two are synchronized, they are not. ';
  out.push(
    surfaceFraming +
      'Edit it ONLY via these tools: ANALYST_WRITE_CELLS (set specific cells/formulas), ' +
      'ANALYST_ADD_SHEET (author a whole table/sheet from rows), ANALYST_REMOVE_SHEET, ' +
      'ANALYST_OPEN_TABLE (switch to another existing table, never recreate one that already exists), ' +
      'ANALYST_CREATE_TABLE (start a brand-new table). ' +
      'A cell whose value starts with "=" is a LIVE formula (standard Excel functions incl. ' +
      'SUM/IF/VLOOKUP/XLOOKUP/INDEX/MATCH/SUMIF(S)/COUNTIF(S) plus IRR/NPV), ' +
      "and may reference other sheets as ='Sheet name'!B2. Prefer live formulas over hardcoded numbers. " +
      'A " →X" suffix after a formula is its CURRENT COMPUTED RESULT (e.g. "B5==SUM(B2:B4) →12,345"); ' +
      'a result shown as "→#DIV/0!" / "→#VALUE!" / "→#REF!" etc. is a LIVE ERROR, inspect its precedent ' +
      'cells and fix it. Trust these computed results over re-deriving them yourself. ' +
      'When your REPLY to the user mentions a specific cell, CITE it as [[Sheet!A1]] (or [[A1]] for the ' +
      'sheet in view), these render as clickable links that jump to and highlight the cell. ' +
      'Use ANALYST_READ_RANGE to pull exact values of a wider range, and ANALYST_FORMAT_CELLS to style ' +
      'cells (bold headers, number formats, highlights). ' +
      'This snapshot is the GROUND TRUTH of what the user is looking at, trust it over your memory: ' +
      'if a sheet shows "(empty)" it really has no data, so do not claim it is populated. ===',
  );
  out.push(`Open table: "${doc.name}", ${doc.sheets.length} sheet${doc.sheets.length === 1 ? '' : 's'}.`);

  // Resolve the in-view sheet from the OPEN doc. st.activeSheetId is a single global
  // value mirrored from the last-rendered grid; right after ANALYST_OPEN_TABLE switches
  // docs it can still point at the previous doc's sheet, which would leave NO sheet
  // matched and every sheet truncated to MAX_CELLS. Fall back to the doc's first sheet
  // so the agent always gets the full snapshot of a real sheet in the open doc.
  const activeSheetId =
    st.activeSheetId && doc.sheets.some((s) => s.id === st.activeSheetId)
      ? st.activeSheetId
      : doc.sheets[0]?.id ?? null;
  doc.sheets.slice(0, MAX_SHEETS).forEach((sh) => {
    const isActive = sh.id === activeSheetId;
    const marker = isActive ? ', CURRENTLY IN VIEW (the sheet the user is looking at)' : '';
    const limit = isActive ? MAX_CELLS_ACTIVE : MAX_CELLS;
    const entries = Object.entries(sh.cells);
    if (entries.length === 0) {
      out.push(`Sheet "${sh.name}"${marker}: (empty, no data yet)`);
      return;
    }
    const computed = wb ? wb[sh.name.toLowerCase()] ?? null : null;
    const cellStr = entries
      .slice(0, limit)
      .map(([k, v]) => {
        const a1 = keyToA1(k);
        const val = v.length > MAX_CELL_LEN ? `${v.slice(0, MAX_CELL_LEN)}…` : v;
        // For formula cells, append the live computed result/error so the agent sees outcomes.
        let suffix = '';
        if (v.startsWith('=') && computed) {
          const f = fmtComputed(computed[k]);
          if (f) suffix = ` →${f}`;
        }
        return `${a1}=${val}${suffix}`;
      })
      .join(', ');
    const more = entries.length > limit ? ` …(+${entries.length - limit} more cells)` : '';
    out.push(`Sheet "${sh.name}"${marker} (cells as A1=value/formula): ${cellStr}${more}`);
  });
  if (doc.sheets.length > MAX_SHEETS) {
    out.push(`…(+${doc.sheets.length - MAX_SHEETS} more sheets not shown)`);
  }

  // Surface a pending ANALYST_READ_RANGE result once, then clear it (consume-once like focusSheet).
  const lastRead = st.consumeLastRead();
  if (lastRead) {
    out.push(
      `=== LAST READ, result of your ANALYST_READ_RANGE of "${lastRead.sheet}"!${lastRead.range} (current computed values) ===\n${lastRead.text}`,
    );
  }

  return out.join('\n');
}

const MAX_INDEX_TABLES = 30;

/**
 * A compact index of ALL Analyst tables (open or not) so the agent can discover
 * and OPEN the right one via ANALYST_OPEN_TABLE instead of recreating it.
 * Returns null when the user has no Analyst tables. This is injected on every
 * Dealer turn (it does NOT force the Analyst model, only an open table does).
 */
export function buildAnalystTablesIndex(): string | null {
  const st = useTablesStore.getState();
  const ids = (st.order && st.order.length ? st.order : Object.keys(st.docs)).filter((id) => st.docs[id]);
  if (ids.length === 0) return null;

  const lines = ids
    .slice(0, MAX_INDEX_TABLES)
    .map((id) => {
      const d = st.docs[id];
      if (!d) return null;
      const sheetNames = d.sheets.map((s) => s.name).slice(0, 12).join(', ');
      const open = st.activeDocId === id ? ' [currently OPEN]' : '';
      return `- "${d.name}" (id: ${id})${open}, sheet${d.sheets.length === 1 ? '' : 's'}: ${sheetNames || '(none)'}`;
    })
    .filter((line): line is string => line !== null);
  const more = ids.length > MAX_INDEX_TABLES ? `\n…(+${ids.length - MAX_INDEX_TABLES} more tables)` : '';

  return (
    '=== ANALYST TABLES, the standalone spreadsheets the user has in the Analyst tool, with their sheets. ' +
    'When the user refers to a table OR a sheet (e.g. "the rent roll we created"), FIRST open it with ' +
    'ANALYST_OPEN_TABLE, its "table" argument accepts a table name/id OR a sheet name (a sheet name opens its ' +
    'containing table and switches to that sheet). Do NOT recreate something that already exists below. ' +
    'This list is the COMPLETE, current set of Analyst tables, if a table or sheet the user mentions ' +
    '(e.g. one from earlier in the chat) is NOT in this list, it no longer exists: create it once with ' +
    'ANALYST_CREATE_TABLE, do NOT keep retrying ANALYST_OPEN_TABLE for it. ' +
    'Then read/edit it with ANALYST_WRITE_CELLS / ANALYST_ADD_SHEET. ===\n' +
    lines.join('\n') +
    more
  );
}
